"use client";

import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Item, ItemActions } from "@/components/ui/item";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";

import {
  ItemsListCardDialog,
  type ItemsListCardFormApi,
  type ItemsListCardSchema,
} from "@/components/backoffice/items-list-card-dialog";

import { PlusIcon, SquarePenIcon, TrashIcon, XIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type BaseItem = {
  id: number;
  isActive: boolean;
};

interface ItemsListCardProps<T extends BaseItem, TCreate> {
  title: string;
  items: T[];
  createSchema: ItemsListCardSchema;
  editSchema: ItemsListCardSchema;
  defaultCreateValues: TCreate;

  pageSize?: number;
  headerExtra?: React.ReactNode;
  canCreate?: boolean;
  emptyMessage?: string;

  renderItem: (item: T) => React.ReactNode;
  renderFormFields: (form: ItemsListCardFormApi<TCreate>) => React.ReactNode;

  onCreate(data: TCreate): Promise<void>;
  onUpdate(data: T): Promise<void>;
  onDelete(id: { id: number }): Promise<void>;
}

export function ItemsListCard<T extends BaseItem, TCreate = Omit<T, "id">>({
  title,
  items,
  createSchema,
  editSchema,
  defaultCreateValues,
  renderItem,
  renderFormFields,
  onCreate,
  onUpdate,
  onDelete,
  pageSize = 2,
  headerExtra,
  canCreate = true,
  emptyMessage = "Aucun résultat trouvé",
}: ItemsListCardProps<T, TCreate>) {
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);

  const [search, _setSearch] = useState("");
  const [page, setPage] = useState(1);

  const setSearch = (value: string) => {
    _setSearch(value);
    setPage(1);
  };

  const openCreate = () => {
    setEditingItem(null);
    setOpen(true);
  };

  const openEdit = (item: T) => {
    setEditingItem(item);
    setOpen(true);
  };

  const filteredItems = items.filter((item) => {
    if (!search) return true;
    return JSON.stringify(item).toLowerCase().includes(search.toLowerCase());
  });

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const showPagination = filteredItems.length > pageSize;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>{title}</CardTitle>

          <div className="flex items-center gap-2">
            {headerExtra}
            <div className="relative w-64">
              <Input
                className="pr-8"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute top-1/2 right-2 -translate-y-1/2"
                >
                  <XIcon size={16} />
                </button>
              )}
            </div>

            <Button onClick={openCreate} disabled={!canCreate}>
              <PlusIcon />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {filteredItems.length === 0 ? (
            <div className="text-muted-foreground py-6 text-center text-sm">
              {emptyMessage}
            </div>
          ) : (
            paginatedItems.map((item) => (
              <Item key={item.id} variant="muted">
                {renderItem(item)}

                <ItemActions>
                  <Badge
                    variant={item.isActive ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() =>
                      onUpdate({
                        ...item,
                        isActive: !item.isActive,
                      })
                    }
                  >
                    {item.isActive ? "Actif" : "Inactif"}
                  </Badge>

                  <ButtonGroup>
                    <Button onClick={() => openEdit(item)}>
                      <SquarePenIcon />
                    </Button>

                    <Button
                      variant="destructive"
                      onClick={() => onDelete({ id: item.id })}
                    >
                      <TrashIcon />
                    </Button>
                  </ButtonGroup>
                </ItemActions>
              </Item>
            ))
          )}

          {showPagination && (
            <div className="flex items-center justify-center gap-3 pt-4 text-sm">
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ←
              </Button>

              <span className="text-muted-foreground">
                {currentPage} / {totalPages}
              </span>

              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                →
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ItemsListCardDialog
        title={title}
        open={open}
        onOpenChange={setOpen}
        item={editingItem}
        createSchema={createSchema}
        editSchema={editSchema}
        defaultCreateValues={defaultCreateValues}
        renderFormFields={renderFormFields}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />
    </>
  );
}

export function ItemsListCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Skeleton className="h-4 w-[200px]" />
        </CardTitle>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <Item key={index}>
              <Skeleton className="h-4 w-[200px]" />
            </Item>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
